import React, { useEffect, useState } from 'react';
import Lamination from './Lamination';
import Trimming from './Trimming';
import Framing from './Framing';
import JBAssembling from './JbAssembling';
import Curing from './Curing';
import Cleaning from './Cleaning';
import ElectricalTest from './ElectricalTest';
import HiPot from './HiPot';
import PostLaminationEL from './Post_Lamination_El';
import VisualInspection from './VisualInspection';
import Packing from './Packing';
import { useNavigate } from 'react-router-dom';

const PostLamination = () => {
    const [activeTab, setActiveTab] = useState('Lamination');
    const navigate = useNavigate();

    // Global fields for shared data across tabs
    const [globalFields, setGlobalFields] = useState({
        date: '',
        shift: '',
        inspectorName: '',
    });

    // Form data for each tab
    const [formData, setFormData] = useState(() => {
        const savedData = sessionStorage.getItem('postLaminationFormData');
        return savedData ? JSON.parse(savedData) : {
            Lamination: {},
            'Trimming/VI at 90 Degree': {},
            Framing: {},
            'JB Assembling & Soldering': {},
            Curing: {},
            Cleaning: {},
            'Electrical Test': {},
            HiPot: {},
            'Post-Lamination EL': {},
            'Visual Inspection': {},
            Packing: {},
        };
    });

    // Persist form data in sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('postLaminationFormData', JSON.stringify(formData));
    }, [formData]);

    // Initialize global fields from session storage
    useEffect(() => {
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');

        setGlobalFields((prevFields) => ({
            ...prevFields,
            date: inspectionDate || '',
            shift: inspectionShift || '',
            inspectorName: inspectorName || '', // Update inspectorName with username from context
        }));
    }, []); // Run once on component mount

    // Handle changes to global fields
    const handleGlobalFieldChange = (e) => {
        const { name, value } = e.target;
        setGlobalFields((prevFields) => ({
            ...prevFields,
            [name]: value,
        }));
    };

    // Handle changes to form data for a specific tab
    const handleFormDataChange = (tab, data) => {
        setFormData((prevData) => ({
            ...prevData,
            [tab]: {
                ...prevData[tab],
                ...data,
            },
        }));
    };

    // Navigation functions
    const goToNextForm = () => {
        navigate('/inspections/section2/form3');
    };

    const goToPreviousForm = () => {
        navigate('/inspections/section1/form1');
    };

    // Render the active tab content
    const renderActiveTab = () => {
        const tabProps = {
            globalFields,
            formData: formData[activeTab],
            onFormDataChange: (data) => handleFormDataChange(activeTab, data),
        };

        switch (activeTab) {
            case 'Lamination':
                return <Lamination {...tabProps} />;
            case 'Trimming/VI at 90 Degree':
                return <Trimming {...tabProps} />;
            case 'Framing':
                return <Framing {...tabProps} />;
            case 'JB Assembling & Soldering':
                return <JBAssembling {...tabProps} />;
            case 'Curing':
                return <Curing {...tabProps} />;
            case 'Cleaning':
                return <Cleaning {...tabProps} />;
            case 'Electrical Test':
                return <ElectricalTest {...tabProps} />;
            case 'HiPot':
                return <HiPot {...tabProps} />;
            case 'Post-Lamination EL':
                return <PostLaminationEL {...tabProps} />;
            case 'Visual Inspection':
                return <VisualInspection {...tabProps} />;
            case 'Packing':
                return <Packing {...tabProps} />;
            default:
                return <Lamination {...tabProps} />;
        }
    };

    // List of tabs
    const tabItems = [
        'Lamination',
        'Trimming/VI at 90 Degree',
        'Framing',
        'JB Assembling & Soldering',
        'Curing',
        'Cleaning',
        'Electrical Test',
        'HiPot',
        'Post-Lamination EL',
        'Visual Inspection',
        'Packing',
    ];

    return (
        <div className="flex max-w-7xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
            {/* Sidebar for Tabs */}
            <div className="w-1/4 bg-[#424752] text-white p-4">
                <h3 className="text-lg font-semibold mb-4">Post-Lamination Tabs</h3>
                <div className="space-y-2">
                    {tabItems.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`w-full text-gray-300 text-left p-3 rounded-lg transition-colors duration-300 ${
                                activeTab === tab
                                    ? 'text-gray-500 bg-white font-bold'
                                    : 'hover:bg-gray-200 hover:text-black'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <div className="w-3/4 p-6">
                {/* Global Fields */}
                <div className="bg-gray-200 p-4 rounded-lg mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-700">Global Fields</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-gray-700 ml-1 font-semibold">Date</label>
                            <input
                                type="date"
                                name="date"
                                value={globalFields.date}
                                onChange={handleGlobalFieldChange}
                                className="border p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                readOnly
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 ml-1 font-semibold">Shift</label>
                            <input
                                type="text"
                                name="shift"
                                placeholder="Shift"
                                value={globalFields.shift}
                                onChange={handleGlobalFieldChange}
                                className="border p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                readOnly
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 ml-1 font-semibold">Inspector Name</label>
                            <input
                                type="text"
                                name="inspectorName"
                                placeholder="Inspector Name"
                                value={globalFields.inspectorName}
                                onChange={handleGlobalFieldChange}
                                className="border p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                readOnly
                            />
                        </div>
                    </div>
                </div>

                {/* Active Tab Content */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-inner">
                    {renderActiveTab()}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-6">
                    <button
                        onClick={goToPreviousForm}
                        className="bg-[#424752] text-white w-[6rem] px-4 py-2 rounded-lg hover:bg-gray-500"
                    >
                        Previous
                    </button>
                    <button
                        onClick={goToNextForm}
                        className="bg-[#424752] text-white w-[6rem] px-4 py-2 rounded-lg hover:bg-gray-500"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PostLamination;